import { useMemo, useState } from 'react';
import { Building2, ChevronDown, Check, Globe } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useApiMachines } from '@/hooks/useMachines';
import { cn } from '@/lib/cn';

export function CompanySwitcher() {
  const user      = useAuthStore((s) => s.user);
  const company   = useAuthStore((s) => s.company);
  const setCompany = useAuthStore((s) => s.setCompany);
  const { data: api = [] } = useApiMachines();
  const [open, setOpen] = useState(false);

  const options = useMemo(() => {
    const set = new Set<string>();
    for (const m of api) set.add(m.company_slug);
    return [...set].sort();
  }, [api]);

  // Hide entirely for non-admins or single-company users.
  if (!user) return null;
  if (!user.isAdmin && options.length <= 1) return null;

  const label = company ?? (user.isAdmin ? 'all companies' : (user.companySlug ?? 'no company'));

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'w-full inline-flex items-center gap-2 px-2 py-1.5 rounded-md border text-xs font-display transition-colors',
          open
            ? 'bg-[var(--accent-brand)] border-[var(--accent-brand)] text-white'
            : 'bg-[var(--bg-base)] border-[var(--border-subtle)] text-[var(--fg-primary)] hover:border-[var(--border-strong)]',
        )}
      >
        <Building2 className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate flex-1 text-left">{label}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-30 rounded-lg border border-[var(--border-strong)] bg-[var(--bg-raised)] shadow-[var(--shadow-lg)] p-1 max-h-64 overflow-auto">
          {user.isAdmin && (
            <button
              onClick={() => { setCompany(null); setOpen(false); }}
              className={cn(
                'w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 transition-colors',
                !company
                  ? 'bg-[#6366F11A] text-[var(--accent-brand)]'
                  : 'text-[var(--fg-muted)] hover:bg-[var(--bg-elevated)] hover:text-[var(--fg-primary)]',
              )}
            >
              <Globe className="w-3 h-3" />
              <span className="font-display">all companies</span>
              {!company && <Check className="w-3 h-3 ml-auto" />}
            </button>
          )}
          {options.map((slug) => {
            const active = company === slug;
            return (
              <button
                key={slug}
                onClick={() => { setCompany(slug); setOpen(false); }}
                className={cn(
                  'w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 transition-colors',
                  active ? 'bg-[#6366F11A] text-[var(--accent-brand)]' : 'text-[var(--fg-muted)] hover:bg-[var(--bg-elevated)] hover:text-[var(--fg-primary)]',
                )}
              >
                <span className={cn('w-1.5 h-1.5 rounded-full', active ? 'bg-[var(--accent-brand)]' : 'bg-[var(--border-strong)]')} />
                <span className="font-display flex-1 truncate">{slug}</span>
                {active && <Check className="w-3 h-3" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
